import { useCallback, useEffect, useState } from "react";
import { FiArrowLeft, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader } from "@/components/common/loader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getSummary, patchFlow, recompute as recomputeApi } from "@/features/oic/api";
import { OicRail } from "@/features/oic/components/oic-rail";
import { EligibilityStep } from "@/features/oic/components/steps/eligibility-step";
import { FinancialsStep } from "@/features/oic/components/steps/financials-step";
import { PacketStep } from "@/features/oic/components/steps/packet-step";
import { RcpStep } from "@/features/oic/components/steps/rcp-step";
import { TermsStep } from "@/features/oic/components/steps/terms-step";
import { useOicFlow } from "@/features/oic/hooks/use-oic-flow";
import { OIC_STEPS, oicStepIndex, type OICStep, type OICSummary } from "@/features/oic/types";
import { useActiveClient } from "@/layouts/client-layout";

const STEP_KEYS = OIC_STEPS.map((s) => s.key);
const isStep = (v: string | undefined): v is OICStep =>
  !!v && (STEP_KEYS as string[]).includes(v);

/**
 * Guided Offer-in-Compromise workflow (milestone 03-14). A 5-step wizard over
 * the Tier 2.2 worksheet offer calc (Financials → RCP → Eligibility → Terms →
 * Packet). The `:step` URL param owns the active step so deep-linking + refresh
 * work. Every dollar figure is read from the backend summary — nothing here is
 * re-derived. Business clients need the Tier 2.2 business worksheet set (not
 * built) — the flow renders but the summary is unavailable for them.
 */
export default function OICWorkflowPage() {
  const { client } = useActiveClient();
  const { caseId, step } = useParams<{ caseId: string; step: string }>();
  const navigate = useNavigate();
  const { flow, loading, error, setFlow, persistStep } = useOicFlow(caseId!);
  const [summary, setSummary] = useState<OICSummary | null>(null);
  const [recomputing, setRecomputing] = useState(false);

  const base = `/client/${client.id}/cases/${caseId}/oic`;
  const active: OICStep = isStep(step) ? step : "financials";

  // Load the assembled RCP summary once the flow exists.
  useEffect(() => {
    if (!flow) return;
    let cancelled = false;
    getSummary(caseId!)
      .then((s) => !cancelled && setSummary(s))
      .catch(() => !cancelled && setSummary(null));
    return () => {
      cancelled = true;
    };
  }, [flow, caseId]);

  // Resume: landing on the base (no/invalid step) routes to the saved step.
  useEffect(() => {
    if (!isStep(step) && flow) {
      navigate(`${base}/${flow.step}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, flow]);

  // Persist the active step as the resume target whenever it changes.
  useEffect(() => {
    if (flow && isStep(step)) void persistStep(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, flow]);

  const onRecompute = useCallback(async () => {
    setRecomputing(true);
    try {
      const res = await recomputeApi(caseId!);
      setFlow(res.flow);
      setSummary(res.summary);
    } catch {
      // Surface via the summary-unavailable UI; a toast would be redundant here.
    } finally {
      setRecomputing(false);
    }
  }, [caseId, setFlow]);

  if (loading) return <Loader className="mt-16" />;
  if (error || !flow) {
    return (
      <Alert variant="destructive" className="mt-8">
        <AlertTitle>Couldn't start the offer flow</AlertTitle>
        <AlertDescription>{error ?? "Please try again."}</AlertDescription>
      </Alert>
    );
  }

  const idx = oicStepIndex(active);
  const ineligible = flow.status === "INELIGIBLE_CSED" || (summary?.expired ?? false);
  const goto = (s: OICStep) => navigate(`${base}/${s}`);

  function renderStep() {
    switch (active) {
      case "rcp":
        return <RcpStep clientId={client.id} caseId={caseId!} summary={summary} />;
      case "eligibility":
        return (
          <EligibilityStep
            clientId={client.id}
            caseId={caseId!}
            flow={flow!}
            summary={summary}
            onFlow={setFlow}
            onContinue={() => goto("terms")}
          />
        );
      case "terms":
        return (
          <TermsStep
            flow={flow!}
            summary={summary}
            onSave={async (patch) => {
              const updated = await patchFlow(caseId!, patch);
              setFlow(updated);
              return updated;
            }}
            onContinue={() => goto("packet")}
          />
        );
      case "packet":
        return (
          <PacketStep
            clientId={client.id}
            caseId={caseId!}
            clientType={client.type === "Business" ? "BUSINESS" : "INDIVIDUAL"}
            flow={flow!}
            onFlow={setFlow}
          />
        );
      default:
        return (
          <FinancialsStep
            clientId={client.id}
            caseId={caseId!}
            summary={summary}
            recomputing={recomputing}
            onRecompute={onRecompute}
          />
        );
    }
  }

  // Next is blocked past Eligibility when the offer is CSED-ineligible.
  const nextLocked =
    ineligible && (active === "eligibility" || active === "terms");

  return (
    <div className="grid gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/client/${client.id}/cases/${caseId}`}>
            <FiArrowLeft className="size-4" /> Back to case
          </Link>
        </Button>
        <h2 className="text-xl font-semibold">Offer in Compromise Workflow</h2>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <OicRail currentStep={active} onSelect={goto} summary={summary} ineligible={ineligible} />

        <div className="grid gap-6">
          <div>
            <p className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
              Step {idx + 1} of {OIC_STEPS.length}
            </p>
            <h3 className="text-lg font-semibold">{OIC_STEPS[idx]!.label}</h3>
          </div>

          {renderStep()}

          <div className="flex items-center justify-between border-t pt-4">
            <Button variant="outline" disabled={idx === 0} onClick={() => goto(OIC_STEPS[idx - 1]!.key)}>
              <FiChevronLeft className="size-4" /> Back
            </Button>
            <Button
              disabled={idx === OIC_STEPS.length - 1 || nextLocked}
              onClick={() => goto(OIC_STEPS[idx + 1]!.key)}
            >
              Next <FiChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
